import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n, countryName } from "@/i18n";

const CountryCitiesMap = lazy(() => import("@/components/CountryCitiesMap"));

export function HomeMapPreview({ t, lp, countrySlug }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) { setInView(true); io.disconnect(); } }, { rootMargin: "200px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!inView) return;
    api.get(`/global/country/${countrySlug}`).then((r) => setD(r.data)).catch(() => {});
  }, [inView, countrySlug]);
  const top = (d?.cities || []).slice(0, 5);
  const { lang } = useI18n();
  const localName = d ? countryName(d.code, lang, d.name) : "";
  return (
    <section ref={ref} className="mx-auto max-w-7xl px-4 py-16 md:px-8" data-testid="landing-map-preview">
      <div className="grid items-start gap-8 lg:grid-cols-[1fr_1.6fr]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3.5 py-1 text-xs font-700 uppercase tracking-wide text-primary"><MapPin className="h-3.5 w-3.5" /> {t("disc.mapBadge")}</span>
          <h2 className="mt-4 font-head text-3xl font-700 md:text-4xl">{t("disc.mapTitle").replace("{name}", localName)}</h2>
          <p className="mt-3 text-muted-foreground">{t("disc.mapLead")}</p>
          {top.length > 0 && (
            <ol className="mt-6 divide-y divide-border/60 rounded-2xl border border-border bg-card" data-testid="home-top-cities">
              {top.map((c, i) => (
                <li key={c.slug}>
                  <Link to={lp(d.city_pages ? `/city/${d.code.toLowerCase()}/${c.slug}` : `/rankings/${countrySlug}`)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent" data-testid={`home-city-${c.slug}`}>
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary font-head text-xs font-800">{i + 1}</span>
                    <span className="flex-1 truncate text-sm font-600">{c.city}</span>
                    <span className="font-mono text-xs"><span className="text-emerald-600 dark:text-success">↓ {c.download}</span> <span className="ml-2 text-muted-foreground">{c.ping} ms</span></span>
                  </Link>
                </li>
              ))}
            </ol>
          )}
          <Link to={lp(`/rankings/${countrySlug}?view=map`)} data-testid="home-map-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-700 text-primary-foreground transition-transform hover:-translate-y-0.5">
            {t("disc.mapCta")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="min-h-[420px]">
          {inView && d ? (
            <Suspense fallback={<div className="h-[420px] animate-pulse rounded-2xl border border-border bg-secondary/50" />}>
              <CountryCitiesMap cities={d.cities} center={d.center} cc={d.code} cityPages={d.city_pages} lp={lp} t={t} />
            </Suspense>
          ) : <div className="h-[420px] animate-pulse rounded-2xl border border-border bg-secondary/50" />}
        </div>
      </div>
    </section>
  );
}
